import { useEffect,useState } from 'react';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import GalleryService from '@/services/gallery.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import FormError from '@/shared-components/error/components/FormError';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import { getAboutErrors } from '../validations';

import ArtworkMediaInput from './components/ArtworkMediaInput';
import ArtworkMultipleInput from './components/ArtworkMultipleInput';
import RequestLimitedEdition from './components/RequestLimitedEdition';
import { ABOUT_INFO_DESCRIPTIONS } from './constants';
import SearchArtist from './search-artist';

import './index.css';

export default function About({ formState, setFormState, editMode, previewMode, formError, setFormError }) {
  const { cacheHandler } = useStateHandler();
  const [loading, setLoading] = useState(false);
  const hideContinueButton = (editMode || previewMode || formState.lastCompletedStep >= 1);
  const [isMobile, setIsMobile] = useState(false);
  const [allFieldsFilled, setAllFieldsFilled] = useState(false);

  useEffect(() => {
    const areAllFieldsFilled = !!(
      formState.artwork_about.artwork_artist &&
      formState.artwork_about.artwork_type &&
      formState.artwork_about.artwork_title &&
      formState.artwork_about.artwork_description &&
      formState.artwork_about.artwork_medium &&
      formState.artwork_about.artwork_year &&
      formState.artwork_about.artwork_color &&
      formState.artwork_about.artwork_theme &&
      formState.artwork_about.artwork_feeling &&
      formState.artwork_about.artwork_size.length &&
      formState.artwork_about.artwork_size.height &&
      formState.artwork_about.artwork_currency &&
      formState.artwork_about.artwork_price
    );
    setAllFieldsFilled(areAllFieldsFilled);
  }, [formState]);
  
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 769);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  function getPreviewStyles() {
    if(!formState.artwork_about.artwork_size.length || !formState.artwork_about.artwork_size.height) return;

    const aspectRatio = formState.artwork_about.artwork_size.length / formState.artwork_about.artwork_size.height;

    if(aspectRatio <= 1) {
      return {
        aspectRatio, 
        height: "100%",
      };
    } else {
      return {
        aspectRatio, 
        width: "100%",
      };
    }
  }

  function validations() {
    setLoading(true);
    setFormError(null);

    const errors = getAboutErrors(formState);

    if (errors.length > 0) {
      setFormError(errors);
      setLoading(false);
      return false;
    }

    return true;
  }

  async function verifyAboutDataAndGoToNextStep() {  
    if (!validations()) return;

    const { response, data } = await GalleryService.saveArtworkDraft(formState, { about: true });

    if(response.ok) {
      setLoading(false); 
      setFormState(prevValue => ({ ...prevValue, lastCompletedStep: prevValue.lastCompletedStep + 1 }));
      cacheHandler.triggerAction("ARTWORK_DRAFT_SAVED");
    } else if(response.status === 400) {
      setLoading(false);
      setFormError([{ element: data.error_data.element, error_code: data.error_type }]);
    }

    if(response.status === 201) setFormState(prevValue => ({ ...prevValue, _id: data._id }));
  }

  const onInputAboutData = (key, value) => {
    setFormState(prevValue => (
      Utils.addAttributeToObject(`artwork_about.${key}`, value, prevValue)
    ));

    setFormError(prevErrors => {
      if (prevErrors === null) {
        return [];
      } else {
        if (key === 'artwork_size.height') { key = 'artwork_height'; }
        if (key === 'artwork_size.length') { key = 'artwork_length'; }
        return prevErrors.filter(error => error.element !== key);
      }
    });
  };

  const descriptions = ABOUT_INFO_DESCRIPTIONS;

  return (
    <>
      <div className={`add-artwork-about-form ${(loading || previewMode) ? "read-only" : ""}`}>
        <PublicFormInput 
          className="add-artwork-about-form__type-of-artwork" 
          type="choose" 
          optionAText={descriptions.find(item => item.name === 'Work of Artist').name} 
          optionBText={descriptions.find(item => item.name === 'Gallery Owned').name} 
          optionA={descriptions.find(item => item.name === 'Work of Artist').value} 
          optionB={descriptions.find(item => item.name === 'Gallery Owned').value} 
          value={formState.artwork_about.artwork_type}
          onChange={(value)=>onInputAboutData("artwork_type", value)}
          headerInfoIcon={descriptions.find(item => item.name === 'Work of Artist').name}
          infoIconDescription={descriptions.find(item => item.name === 'Work of Artist').description}
          headerInfoIcon2={descriptions.find(item => item.name === 'Gallery Owned').name}
          infoIconDescription2={descriptions.find(item => item.name === 'Gallery Owned').description}
        />

        <SearchArtist 
          error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_artist') : null} 
          element="artwork_artist" 
          value={formState.artwork_about.artwork_artist} 
          typeOfArtwork={formState.artwork_about.artwork_type}
          onChange={(value)=>onInputAboutData("artwork_artist", value)}
          infoIcon={descriptions.find(item => item.name === 'Artist')}
        />

        <PublicFormInput 
          error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_title'): null} 
          placeholder="Title" 
          element="artwork_title" 
          value={formState.artwork_about.artwork_title} 
          onChange={(e)=>onInputAboutData("artwork_title", e.target.value)}
          margin
          infoIconDescription={descriptions.find(item => item.name === 'Title').description}
        />

        <PublicFormInput
          className="add-artwork-about-form__description" 
          error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_description'): null} 
          placeholder="Description" 
          type="textarea" 
          element="artwork_description" 
          readOnly={previewMode}
          value={formState.artwork_about.artwork_description} 
          onChange={(e)=>onInputAboutData("artwork_description", e.target.value)}
          margin
          infoIconDescription={descriptions.find(item => item.name === 'Description').description}
        />

        <div className="add-artwork-about-form__group select">
          <ArtworkMediaInput 
            error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_medium'): null} 
            previewMode={previewMode} 
            element="artwork_medium"
            value={formState.artwork_about.artwork_medium} 
            onChange={(value)=>onInputAboutData("artwork_medium", value)}
            margin
            infoIcon={descriptions.find(item => item.name === 'Medium')}
          />

          <PublicFormInput 
            className={'artwork-media-input__outter-container margin'}
            error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_year'): null} 
            placeholder="Year of Creation" 
            element="artwork_year" 
            value={formState.artwork_about.artwork_year} 
            onChange={(e)=>onInputAboutData("artwork_year", e.target.value)}
            infoIconDescription={descriptions.find(item => item.name === 'Year of Creation').description}
          />
        </div>

        <div className='add-artwork-about-form__group'>
          <div className='add-artwork-about-form__group-medium variant'>
            <ArtworkMultipleInput 
              error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_color'): null} 
              placeholder="Color(s)" 
              type="colors" 
              options={config.forms.color_options} 
              value={formState.artwork_about.artwork_color} 
              element="artwork_color" 
              previewMode={previewMode} 
              onChange={(value)=>onInputAboutData("artwork_color", value)}
              margin
              infoIcon={descriptions.find(item => item.name === 'Color')}
            />
          
            <ArtworkMultipleInput 
              error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_theme'): null} 
              placeholder="Theme(s)" 
              type="themes" 
              options={config.forms.theme_options} 
              value={formState.artwork_about.artwork_theme} 
              element="artwork_theme" 
              previewMode={previewMode} 
              onChange={(value)=>onInputAboutData("artwork_theme", value)}
              margin
              infoIcon={descriptions.find(item => item.name === 'Theme')}
            />
          
            <ArtworkMultipleInput 
              placeholder="Emotion(s)" 
              type="emotions" 
              options={config.forms.feeling_options} 
              value={formState.artwork_about.artwork_feeling} 
              element="artwork_feeling" 
              error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_feeling'): null} 
              previewMode={previewMode} 
              onChange={(value)=>onInputAboutData("artwork_feeling", value)}
              margin
              infoIcon={descriptions.find(item => item.name === 'Emotion')}
            />
            
            <div className="add-artwork-about-form__group currency-year">
              <PublicFormInput 
                className="add-artwork-about-form__group-smaller variant add-artwork-about-form__group-margin" 
                error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_currency'): null} 
                placeholder="Currency" 
                type="select" 
                element="artwork_currency" 
                selectOptions={{ options: config.forms.currency_options }} 
                readOnly={previewMode} 
                value={formState.artwork_about.artwork_currency} 
                onChange={(value)=>onInputAboutData("artwork_currency", value)}
                margin
              />

              <PublicFormInput 
                className="add-artwork-about-form__group-bigger variant" 
                error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_price'): null} 
                placeholder="Price (excluding taxes)" 
                element="artwork_price" 
                value={formState.artwork_about.artwork_price} 
                onChange={(e) => {
                  if(/^[0-9]*$/.test(e.target.value)) {
                    onInputAboutData("artwork_price", e.target.value);
                  }
                }}
                infoIconDescription={descriptions.find(item => item.name === 'Price').description}
                margin 
              />
            </div>
          </div>
          <div className="add-artwork-about-form__group-outter">
            <div>
              <div className="add-artwork-about-form__group center" id="artwork_size">
                <PublicFormInput 
                  className="add-artwork-about-form__choose-input" 
                  type="choose" 
                  optionA="cm" 
                  optionB="in" 
                  value={formState.artwork_about.artwork_size.unit} 
                  onChange={(value) => onInputAboutData("artwork_size.unit", value)}
                  headerInfoIcon="centimeters / inches"
                  infoIconDescription={descriptions.find(item => item.name === 'Dimensions').description}
                />
                              
                <PublicFormInput 
                  error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_length') : null} 
                  placeholder={`Width (${formState.artwork_about.artwork_size.unit})`}
                  element="artwork_length" 
                  type="number" 
                  value={formState.artwork_about.artwork_size.length} 
                  onChange={(e) => onInputAboutData("artwork_size.length", e.target.value)}
                />

                {!isMobile && <Text className="add-artwork-about-form__size-separator" large>x</Text>}

                <PublicFormInput 
                  error={Array.isArray(formError) ? formError?.find(error => error.element === 'artwork_height') : null} 
                  placeholder={`Height (${formState.artwork_about.artwork_size.unit})`}
                  element="artwork_height" 
                  type="number" 
                  value={formState.artwork_about.artwork_size.height} 
                  onChange={(e) => onInputAboutData("artwork_size.height", e.target.value)}
                />
              </div>
              <FormError error={formError} element="artwork_size"/>

            </div>
            <div className="square-start">
              <div className={`${Array.isArray(formError) && (formError?.find(error => error.element === 'artwork_height') || formError?.find(error => error.element === 'artwork_length')) ? 'square-container-error' : 'square-container'}`}>
                <div className="square" style={getPreviewStyles()}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Adjusted condition to allow Limited Editions activation in edit mode  Pablo*/}
        {(!editMode || !formState.artwork_about.limitedEditionsAccepted) && (
          <RequestLimitedEdition 
            previewMode={previewMode} 
            unit={formState.artwork_about.artwork_size.unit} 
            height={formState.artwork_about.artwork_size.height} 
            length={formState.artwork_about.artwork_size.length} 
            price={formState.artwork_about.artwork_price} 
            typeOfArtwork={formState.artwork_about.artwork_type}
            value={formState.artwork_about.artwork_limited_edition}
            onChange={(value) => onInputAboutData("artwork_limited_edition", value)} 
            infoIcon={descriptions.find(item => item.name === 'Limited Editions')}
            approved={Object.keys(formState?.artwork_limited_editions || {}).length > 0 }
          />
        )}

        <PublicFormInput 
          element="artwork_includes_certificate" 
          type="checkbox" 
          className="add-artwork-about-form__checkbox-margin"
          value={formState.artwork_about.artwork_includes_certificate} 
          onChange={(value)=>onInputAboutData("artwork_includes_certificate", value)} 
        >
          Certificate of Authenticity included  
        </PublicFormInput>

        <PublicFormInput 
          element="artwork_includes_frame" 
          type="checkbox"
          value={formState.artwork_about.artwork_includes_frame} 
          onChange={(value)=>onInputAboutData("artwork_includes_frame", value)}
        >
          Frame included    
        </PublicFormInput>

        {!hideContinueButton && (
          <ContinueButton 
            onClick={verifyAboutDataAndGoToNextStep} 
            loading={loading} 
            link
            disabled={!allFieldsFilled}
          >
            Continue
          </ContinueButton>
        )}
      </div>
    </>
  );
}